import { useMemo } from 'react';

import { Cluster } from '@cast/types';

import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { StorageDiskCharts } from '../../_components/StorageDiskCharts';
import { useNodeStorageMetricsQuery } from 'hooks/queries/nodes/useNodeStorageMetricsQuery';
import { ManagedDiskAllCurrentUsage } from './ManagedDiskAllCurrentUsage';

import { useNodeStorageTimeRangeSelection } from '../../_hooks/useNodeStorageTimeRangeSelection';
import { resolveMetricsStep } from '../../_util';
import { Typography, Stack } from '@cast/design-system';

const SYNC_ID = 'all-managed-disk-volume-charts';

type ManagedDiskAllTabContentProps = {
  node?: NodeWithDependencies;
  cluster: Cluster;
  isCloudDisksEnabled?: boolean;
};

export const ManagedDiskAllTabContent = ({
  node,
  cluster,
  isCloudDisksEnabled,
}: ManagedDiskAllTabContentProps) => {
  const { getRange, key, timeZone } = useNodeStorageTimeRangeSelection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { from, to } = useMemo(() => getRange(), [key]);

  const { series, summary, isLoading, error, refetch } =
    useNodeStorageMetricsQuery({
      clusterId: cluster.id,
      nodeName: node?.name,
      storageType: 'managed-ephemeral',
      startTime: from,
      endTime: to,
      step: resolveMetricsStep(from, to),
    });

  return (
    <Stack gap={16}>
      <ManagedDiskAllCurrentUsage
        node={node}
        cluster={cluster}
        isCloudDisksEnabled={isCloudDisksEnabled}
      />

      <Stack gap={8}>
        <Typography variant="A1" color="textSecondary">
          Usage over time
        </Typography>
        <StorageDiskCharts
          series={series}
          summary={{
            usedBytes: summary?.usedBytes?.avg,
            provisionedSizeBytes: summary?.provisionedSizeBytes?.avg,
            throughputBytes: summary?.throughputBytes?.avg,
            iops: summary?.iops?.avg,
            provisionedIops: summary?.provisionedIops?.avg,
            provisionedThroughputBytes:
              summary?.provisionedThroughputBytes?.avg,
          }}
          isLoading={isLoading}
          error={error}
          refetch={refetch}
          syncId={SYNC_ID}
          timeZone={timeZone}
          isNodeReady={!!node}
          isCloudDisksEnabled={isCloudDisksEnabled}
          labels={{
            capacity: {
              label: 'Auto-expanding',
              tooltip:
                'Expands automatically when usage approaches the provisioned limit.',
            },
          }}
        />
      </Stack>
    </Stack>
  );
};
